import React, { useState, useEffect } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import Feedback from "./Feedback";
import Information from "./Information";
import UserMenu from "./UserMenu";
import { useNavigate, useLocation } from "react-router-dom";

function Header({
  activeTab,
  handleTabChange,
  toggleMenu,
  isMenuOpen,
  handleLogout,
  geocoderContainerRef,
  showSearch = false,
  showFavorites = false,
  showLinks = false,
  handleUpgrade,
  isSubscribed = false,
  selectedCounty,
  countiesList = [],
  onCountyChange,
  showCountySelector = false,
  onParcelSearch,
  clearParcelSearch,
  setIsParcelSearchActive,
  resetParcelSearchSignal = null,
  onAdjacentToggle,
  isAdjacentMode = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [parcelInput, setParcelInput] = useState("");
  const [parcelError, setParcelError] = useState("");
  const [isParcelValid, setIsParcelValid] = useState(false);
  const prevLengthRef = React.useRef(0);

  const storedName = localStorage.getItem("name") || "User";
  const storedEmail = localStorage.getItem("email") || "unknown";

  const isNCPage = location.pathname.toLowerCase().endsWith("/nc");
  const isDashboardPage = location.pathname.toLowerCase().endsWith("/dashboard");
  const isAdjacentParcels = location.pathname.endsWith("/GroupedParcels");

  const effectiveAdjacent =
    typeof onAdjacentToggle === "function" && typeof isAdjacentMode === "boolean"
      ? isAdjacentMode
      : isAdjacentParcels;

  const PARCEL_LENGTH_BY_COUNTY = {
    default: 7,
    wake: 10,
  };

  const getMinParcelLength = (county) => {
    if (!county) return PARCEL_LENGTH_BY_COUNTY.default;
    return (
      PARCEL_LENGTH_BY_COUNTY[county.toLowerCase()] || PARCEL_LENGTH_BY_COUNTY.default
    );
  };

  const minLength = getMinParcelLength(selectedCounty);

  useEffect(() => {
    if (resetParcelSearchSignal === null) return;
    setParcelInput("");
    setParcelError("");
    setIsParcelValid(false);
    setIsParcelSearchActive(false);
  }, [resetParcelSearchSignal, setIsParcelSearchActive]);

  useEffect(() => {
    if (activeTab === "all" && geocoderContainerRef?.current) {
      if (
        geocoderContainerRef.current.childNodes.length === 0 &&
        window.geocoderInstance
      ) {
        geocoderContainerRef.current.appendChild(
          window.geocoderInstance.onAdd(window.initializedMap)
        );
      }
    }
  }, [activeTab, geocoderContainerRef]);

  const handleParcelInputChange = (value) => {
    const digits = value.replace(/\D/g, "");
    const currentLength = digits.length;
    setParcelInput(digits);
    prevLengthRef.current = currentLength;

    if (currentLength === 0) {
      setIsParcelValid(false);
      setIsParcelSearchActive(false);
      onParcelSearch && onParcelSearch("");
      return;
    }

    if (currentLength < minLength) {
      setIsParcelValid(false);
      setIsParcelSearchActive(false);
      setParcelError(`Enter at least ${minLength} digits`);
      return;
    }

    setIsParcelValid(true);
    setParcelError("");
    setIsParcelSearchActive(true);
    onParcelSearch && onParcelSearch(digits);
  };

  const handleParcelClear = () => {
    setParcelInput("");
    setIsParcelValid(false);
    setIsParcelSearchActive(false);
    setParcelError("");
    clearParcelSearch && clearParcelSearch();
  };

  const handleParcelViewToggle = (event) => {
    const checked = event.target.checked;
    if (typeof onAdjacentToggle === "function") {
      onAdjacentToggle(checked);
      return;
    }
    if (checked) navigate("/GroupedParcels");
    else navigate("/nc");
  };

  const handleUpgrade1 = () => {
    navigate("/pricing");
  };

  return (
    <div className="headerTop">
      <div
        className="dflex subHeaderTop"
        style={
          showCountySelector
            ? {
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                gap: "20px",
              }
            : {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }
        }
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "fit-content",
            zIndex: 10,
            textDecoration: "none",
          }}
        >
          <img className="logo" src="/assets/Flowlogic-Logo.svg" alt="FlowLogic Icon" />
          <span
            style={{
              marginLeft: "30px",
              fontSize: "20px",
              fontWeight: "500",
              color: "#333",
              borderLeft: "1px solid #000",
              paddingLeft: "20px",
            }}
          >
            {isDashboardPage ? "FlowMap" : isNCPage ? " Parcels" : ""}
          </span>
        </div>

        {showCountySelector && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: selectedCounty !== "all" ? "0px" : "14px",
              position: "relative",
              width: "100%",
            }}
          >
            {selectedCounty && selectedCounty !== "all" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: "28px 0px 0px 28px",
                  background: "#fff",
                  padding: "8px 12px",
                  borderRight: "none",
                  flexGrow: 1,
                  boxShadow: " rgba(0,0,0,0.1) 0px 0px 0px 2px",
                  borderRight: "none",
                }}
              >
                <img
                  src="/assets/search.svg"
                  alt="Parcel search"
                  style={{ width: 20, height: 20, opacity: 0.6, cursor: "default" }}
                />
                
                <input
                  type="text"
                  placeholder="Enter parcel #"
                  value={parcelInput}
                  onChange={(e) => handleParcelInputChange(e.target.value)}
                  style={{
                    marginLeft: 8,
                    border: "none",
                    outline: "none",
                    width: "100%",
                    fontSize: 13,
                    height: "22px",
                    color: isParcelValid ? "#1c8f7d" : parcelInput ? "#e53935" : "#333",
                    background: "transparent",
                  }}
                />

                {parcelInput && (
                  <button
                    type="button"
                    onClick={handleParcelClear}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      // marginLeft: 4,
                      padding: 0,
                    }}
                  >
                    <img src="/assets/close.svg" alt="Clear" style={{ width: 14, height: 14 }} />
                  </button>
                )}
              </div>
            )}

            <select
              className="county-dropdown-header"
              value={selectedCounty}
              onChange={onCountyChange}
              style={{
                width: "200px",
                height: "40px",
                padding: "0 14px",
                borderRadius: selectedCounty !== "all" ? "0px 28px 28px 0px" : "28px",
                boxShadow: selectedCounty !== "all" ? "0px -2px 0px rgba(0,0,0,0.1), 2px 0px 0px rgba(0,0,0,0.1), 0px 2px 0px rgba(0,0,0,0.1)" : "rgba(0,0,0,0.1) 0px 0px 0px 2px",
                border: "1px solid rgba(0,0,0,0.1)",
                fontSize: "14px",
                background: "#fff",
                color: "#333",
                cursor: "pointer",
                fontWeight: 400,
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                outline: "none",
                backgroundImage:
                  "url('data:image/svg+xml,%3Csvg width=\"10\" height=\"6\" viewBox=\"0 0 10 6\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath fill=\"%23666\" d=\"M0 0l5 6 5-6z\"/%3E%3C/svg%3E')",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 14px center",
                borderLeft: selectedCounty !== "all" ? "none" : "1px solid rgba(0,0,0,0.1)",
                textAlign: selectedCounty !== "all" ? "right" : "left",
                paddingRight: selectedCounty !== "all" ? "40px" : "14px",
              }}
            >
              <option value="all" style={{ paddingRight: "10px" }}>All North Carolina</option>
              {countiesList.map((c) => (
                <option key={c} value={c.toLowerCase()} style={{ paddingRight: "10px" }}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}

        <div
          className="headerRightOuter"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            justifySelf: "flex-end",
            width: "fit-content",
          }}
        >
          <div className="headerMiddleOuter">
            {showCountySelector && selectedCounty !== "all" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginRight: "18px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#666",
                }}
              >
                <span
                  onClick={() => handleParcelViewToggle({ target: { checked: false } })}
                  style={{
                    cursor: "pointer",
                    padding: "6px 0",
                    borderBottom: !effectiveAdjacent
                      ? "2px solid #1C8F7D"
                      : "2px solid transparent",
                    color: !effectiveAdjacent ? "#1C8F7D" : "#666",
                  }}
                >
                  Parcels
                </span>
                <span style={{ color: "#1C8F7D" }}>|</span>
                <span
                  onClick={() => handleParcelViewToggle({ target: { checked: true } })}
                  style={{
                    cursor: "pointer",
                    padding: "6px 0",
                    borderBottom: effectiveAdjacent
                      ? "2px solid #1C8F7D"
                      : "2px solid transparent",
                    color: effectiveAdjacent ? "#1C8F7D" : "#666",
                  }}
                >
                  Assemblage
                </span>
              </div>
            )}

            {showFavorites && (
              <a
                onClick={() => handleTabChange(activeTab === "favorites" ? "all" : "favorites")}
                className={`tab-button favorites ${
                  activeTab === "favorites" ? "favorites-active" : ""
                }`}
              >
                <img
                  src={activeTab === "favorites" ? "/assets/favorite.svg" : "/assets/heart.svg"}
                  alt="My favorites"
                />
                Favorites
              </a>
            )}

            <div className="iconGroup">
              {!isSubscribed && (
                <button className="upgrade-btn" onClick={handleUpgrade1}>
                  Upgrade
                </button>
              )}
            </div>

            {showLinks && (
              <div className="iconGroup">
                <Information />
                <Feedback />
              </div>
            )}
          </div>

          <UserMenu
            isMenuOpen={isMenuOpen}
            toggleMenu={toggleMenu}
            handleLogout={handleLogout}
            isDashboard={isDashboardPage}
          />
        </div>
      </div>

      {showSearch && !showCountySelector && activeTab !== "favorites" && (
        <div className="search">
          <div ref={geocoderContainerRef} className="geoCodeSerach" />
        </div>
      )}
    </div>
  );
}

export default Header;
